import classNames from 'classnames';
import React from 'react';

import useTranslation from '../../hooks/useTranslation';
import { FormattedFuturePayment } from '../../utils/mapper/futurepaymentsUtil';
import { PaymentLimitsErrorMessage } from '../shared/PaymentLimitsErrorMessage';
import { SelectList } from '../shared/SelectList';
import { currencyFormatter, numberFormat } from '../shared/numberFormat';
import FuturePaymentListMonthElement from './FuturePaymentListMonthElement';

import './FuturePaymentsListMonth.scss';

interface Props extends React.PropsWithChildren {
    label: string;
    futurePayments: FormattedFuturePayment[];
    onClick: (futurePayment: FormattedFuturePayment) => void;
    availableBalance?: number;
    showFromAccount?: boolean;
    labelClassName?: string;
    showPaymentLimitWarning?: boolean;
    totalAmountOut?: number;
    totalAmountIn?: number;
}

const FuturePaymentsListMonth: React.FC<Props> = ({
    label,
    futurePayments,
    onClick,
    availableBalance,
    totalAmountOut,
    totalAmountIn,
    showFromAccount = false,
    children,
    labelClassName,
    showPaymentLimitWarning = false,
}) => {
    const translated = useTranslation();

    const hasAvailableBalance = !!availableBalance && !children;
    const hasTotalAmount = !!totalAmountIn || !!totalAmountOut;

    return (
        <li className="future-payments-list-month__container">
            <div className={classNames('future-payments-list-month__label-and-amount', labelClassName)}>
                <div className="future-payments-list-month__label bold">{label}</div>

                {!hasAvailableBalance && children}
                {hasAvailableBalance && (
                    <div className="future-payments-list-month__current-amount">
                        {translated.futurePayments.balanceToday} {numberFormat(availableBalance)}
                    </div>
                )}
                {showPaymentLimitWarning && (
                    <PaymentLimitsErrorMessage
                        className="future-payments-list-month__payment-limit-message"
                        type="attention"
                        message={translated.futurePaymentLimitWarning}
                    />
                )}
            </div>
            <SelectList>
                {futurePayments.map((payment) => (
                    <li key={payment.id}>
                        <FuturePaymentListMonthElement
                            showFromAccount={showFromAccount}
                            payment={payment}
                            onClick={onClick}
                        />
                    </li>
                ))}
            </SelectList>
            {hasTotalAmount && (
                <div className="future-payments-list-month__current-amount">
                    <span>{`${translated.futurePaymentsList.totalIn}  ${currencyFormatter(totalAmountIn, 'NOK')}`}</span>
                    <div className="vertical-line" />
                    <span>
                        {`${translated.futurePaymentsList.totalOut}  ${currencyFormatter(totalAmountOut, 'NOK')}`}
                    </span>
                </div>
            )}
        </li>
    );
};

export default FuturePaymentsListMonth;
